/**
 * Flow diagrams for the printed case report, drawn as inline SVG from the
 * diagram IR (no bundled Mermaid; OD13 as directed for W3-08).
 *
 * The IR declares meaning only: each node's entity bucket (`entity_class`),
 * placement role (`placement_role`, R24) and, when the run carries it, phase;
 * each edge's `line_kind`. This module owns presentation: a deterministic
 * layered flowchart layout that runs left to right, the shapes, colours and the
 * legend. Same IR in, same SVG out.
 *
 * Lanes: one labelled band per phase (inflow, corridor, upstream, subject,
 * layering, off-ramp), left to right in the order money moves. A node whose
 * phase is not declared goes to a band labelled "Not phased", never into
 * another. When no node carries a phase (executive kind, older stored runs) the
 * bands come from the declared placement roles instead (funds in, subject,
 * relays, exits). Inside a band, nodes are layered by longest path. An edge
 * that spans several columns gets a virtual waypoint in each column it crosses,
 * so it is routed through free space and never over a node. Vertical order
 * comes from barycentre sweeps and an order-preserving, minimum-gap placement.
 * Edges that run against the flow (a cycle, or into an earlier band) leave by
 * the right side, travel in the gap channels and under the nodes, and enter by
 * the left side. Edge labels (amount, short date) are placed on the edge in
 * free space and checked against every node and every earlier label; a label
 * that cannot be placed is left to the tooltip and counted in the notes.
 * Coordinates are rounded so the output is stable.
 */
import { esc, buildCollapsedCodeBlock } from '../../utils/reportCommon';
import type { FlowDiagramEdge, FlowDiagramIR, FlowDiagramNode } from './types';
import { figureWalletBlock, flowFactsLegend, hasFlowFacts, nodeLineList } from './flowFacts';
import { diagramPalette } from '../../constants/diagramPalette';

/** Fill / stroke / text per entity bucket. Report presentation only. */
export const DIAGRAM_CLASS_STYLE: Record<string, { fill: string; stroke: string; text: string; label: string; dashed?: boolean }> = {
  victim: { fill: '#fee2e2', stroke: '#dc2626', text: '#991b1b', label: 'Victim' },
  subject: { fill: '#fef08a', stroke: '#ca8a04', text: '#713f12', label: 'Subject' },
  cex: { fill: '#ffedd5', stroke: '#ea580c', text: '#9a3412', label: 'Exchange or service' },
  mule: { fill: '#f3e8ff', stroke: '#9333ea', text: '#6b21a8', label: 'Wallet (relay or mule)' },
  bridge: { fill: '#e0f2fe', stroke: '#0284c7', text: '#075985', label: 'Bridge' },
  contract: { fill: '#ecfeff', stroke: '#0e7490', text: '#164e63', label: 'Contract that received the value of the case', dashed: true },
  btc: { fill: '#fef9c3', stroke: '#a16207', text: '#713f12', label: 'UTXO-chain address' },
  mixer: { fill: '#27272a', stroke: '#a855f7', text: '#f4f4f5', label: 'Mixer or privacy pool' },
  spam: { fill: '#f1f5f9', stroke: '#94a3b8', text: '#475569', label: 'Poison or spam address', dashed: true },
  // Report-only buckets (the action / notice targets diagram).
  target_wallet: { fill: '#f8fafc', stroke: '#475569', text: '#0f172a', label: 'Target wallet' },
  recipient: { fill: '#ffedd5', stroke: '#ea580c', text: '#9a3412', label: 'Notice recipient' },
  notice: { fill: '#dcfce7', stroke: '#16a34a', text: '#14532d', label: 'Notice (evidence complete)' },
  notice_missing: { fill: '#fef2f2', stroke: '#dc2626', text: '#991b1b', label: 'Notice blocked: evidence missing', dashed: true },
  advisory: { fill: '#fef3c7', stroke: '#d97706', text: '#78350f', label: 'Advisory (threat warning)' },
  unknown_upstream: { fill: '#f1f5f9', stroke: '#64748b', text: '#334155', label: 'Unknown upstream (origin not traced)', dashed: true },
  cex_deposit_verified: {
    fill: diagramPalette.cex_deposit_verified.fill,
    stroke: diagramPalette.cex_deposit_verified.stroke,
    text: diagramPalette.cex_deposit_verified.text,
    label: 'Confirmed exchange deposit',
  },
};
const DEFAULT_STYLE: { fill: string; stroke: string; text: string; label: string; dashed?: boolean } = { fill: '#f8fafc', stroke: '#64748b', text: '#0f172a', label: 'Other' };

export const LINE_KIND_STYLE: Record<string, { width: number; dash?: string; label: string }> = {
  solid: { width: 1.4, label: 'Transfer (ledger fact)' },
  dotted: { width: 1.4, dash: '3 3', label: 'Corroboration (gas funding, residual return)' },
  thick: { width: 3, label: 'Aggregated channel total' },
  missing: { width: 1.4, dash: '2 5', label: 'Evidence missing (not traced or no linking transfer yet)' },
  inferred: { width: 1.4, dash: '6 4', label: 'Inferred link (scored, not a ledger fact)' },
};

const ROLE_RANK: Record<string, number> = { source: 0, subject: 1, relay: 2, shared_relay: 2, sink: 3, context: 4 };

const BOX_W = 156;
const GAP = 124;
const COL_W = BOX_W + GAP;
const ROW_GAP = 18;
const WAYPOINT_H = 4;
const WAYPOINT_GAP = 14;
const LINE_H = 14;
const PAD_X = 16;
const PAD_Y = 16;
const HEADER_H = 30;
const MAX_LABEL_LINES = 3;
const DEFAULT_MAX_NODES = 80;
const EDGE_LABEL_LIMIT = 120;

export interface LaneBand { key: string; label: string; x: number; w: number; count: number }
export interface LaidOutNode { node: FlowDiagramNode; layer: number; lane: string; x: number; y: number; w: number; h: number }
export interface EdgeLabel { x: number; y: number; w: number; h: number; lines: string[] }
export interface LaidOutEdge { edge: FlowDiagramEdge; back: boolean; path: string; label: EdgeLabel | null }
export interface FlowDiagramLayout {
  nodes: LaidOutNode[];
  edges: LaidOutEdge[];
  lanes: LaneBand[];
  width: number;
  height: number;
  layers: number;
  omittedNodes: number;
  omittedSelfLoops: number;
  /** Edges whose label found no free space; the tooltip still carries it. */
  omittedLabels: number;
  /** 'phase': bands are the declared phases; 'hops': no phase declared, bands are hops from the subject along the edges; 'role': no subject either, bands are the declared roles. */
  laneBasis: 'phase' | 'hops' | 'role';
}

const r1 = (n: number) => Math.round(n * 10) / 10;

const PHASE_LANES: [string, string][] = [
  ['evidence', 'Evidence'], ['inflow', 'Victim inflow'], ['corridor', 'Corridor'], ['upstream', 'Upstream'],
  ['subject', 'Subject'], ['layering', 'Layering'], ['offramp', 'Off-ramp'], ['action', 'Action'],
];
const ROLE_LANES: [string, string][] = [
  ['source', 'Funds in'], ['subject', 'Subject'], ['relay', 'Relays'], ['sink', 'Exits'], ['context', 'Not placed'],
];
/** Phase lane order: 'layering_first' (default) puts layering before off-ramp; 'offramp_first' swaps those two. */
export type LaneOrder = 'layering_first' | 'offramp_first';
export function phaseLanes(order?: LaneOrder): [string, string][] {
  if (order !== 'offramp_first') return PHASE_LANES;
  const out = PHASE_LANES.slice();
  const a = out.findIndex(([k]) => k === 'layering'); const b = out.findIndex(([k]) => k === 'offramp');
  [out[a], out[b]] = [out[b], out[a]];
  return out;
}
const NOT_PHASED = '~not_phased';
const isUnplaced = (k: string) => k === NOT_PHASED || k === 'hop:none';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function clipText(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/** Drawn lines of an edge label: the amount lines as the IR words them, then the short date. The tx reference stays in the tooltip. */
export function edgeLabelLines(e: FlowDiagramEdge): string[] {
  const main: string[] = [];
  let date = '';
  let more = '';
  for (const raw of e.label_lines || []) {
    const l = String(raw || '').trim();
    if (!l || /^tx\s/i.test(l)) continue;
    // "+N more transfers": the amount is the sum of several transfers under the one hash the label names. It keeps its
    // place on the drawn label ahead of the USD line, which the tooltip still carries.
    if (/^\+\d+ more transfers?$/i.test(l)) { more = l; continue; }
    const m = DATE_RE.exec(l);
    if (m) { date = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] || m[2]} ${m[1]}`; continue; }
    main.push(clipText(l, 30));
  }
  const out = more ? [...main.slice(0, 1), more] : main.slice(0, 2);
  if (date) out.push(date);
  return out;
}

interface Item { id: string; real: boolean; col: number; h: number; y: number; order: number }

function boxHeight(n: FlowDiagramNode): number {
  const lines = Math.max(1, Math.min(MAX_LABEL_LINES, (n.label_lines || []).length));
  return 14 + lines * LINE_H;
}

function cubicAt(p: number[][], t: number): [number, number] {
  const u = 1 - t;
  const f = (i: number) => u * u * u * p[0][i] + 3 * u * u * t * p[1][i] + 3 * u * t * t * p[2][i] + t * t * t * p[3][i];
  return [f(0), f(1)];
}

/** Order-preserving placement with minimum gaps, closest (least squares) to the desired centres: pool adjacent violators. */
function placeColumn(items: Item[], desiredCenter: (it: Item) => number, gapOf: (a: Item, b: Item) => number): void {
  const m = items.length;
  if (!m) return;
  const off: number[] = [0];
  for (let i = 1; i < m; i++) off.push(off[i - 1] + items[i - 1].h + gapOf(items[i - 1], items[i]));
  const blocks: { sum: number; n: number; from: number }[] = [];
  for (let i = 0; i < m; i++) {
    blocks.push({ sum: desiredCenter(items[i]) - items[i].h / 2 - off[i], n: 1, from: i });
    while (blocks.length > 1) {
      const b = blocks[blocks.length - 1]; const a = blocks[blocks.length - 2];
      if (a.sum / a.n <= b.sum / b.n) break;
      blocks.splice(blocks.length - 2, 2, { sum: a.sum + b.sum, n: a.n + b.n, from: a.from });
    }
  }
  blocks.forEach((b, bi) => {
    const to = bi + 1 < blocks.length ? blocks[bi + 1].from : m;
    for (let i = b.from; i < to; i++) items[i].y = off[i] + b.sum / b.n;
  });
}

type Rect = { x: number; y: number; w: number; h: number };

/** Deterministic left-to-right layered layout of a diagram IR. */
export function layoutFlowDiagram(ir: FlowDiagramIR, opts: { maxNodes?: number; laneOrder?: LaneOrder } = {}): FlowDiagramLayout {
  const maxNodes = opts.maxNodes ?? DEFAULT_MAX_NODES;
  const seen = new Set<string>();
  let nodes: FlowDiagramNode[] = [];
  for (const n of ir.nodes || []) {
    if (!n || typeof n.id !== 'string' || seen.has(n.id)) continue;
    seen.add(n.id);
    nodes.push(n);
  }
  let omittedSelfLoops = 0;
  let edges = (ir.edges || []).filter((e) => {
    if (!e || !seen.has(e.source) || !seen.has(e.target)) return false;
    if (e.source === e.target) { omittedSelfLoops += 1; return false; }
    return true;
  });

  let omittedNodes = 0;
  if (nodes.length > maxNodes) {
    const degree = new Map<string, number>();
    for (const e of edges) {
      degree.set(e.source, (degree.get(e.source) || 0) + 1);
      degree.set(e.target, (degree.get(e.target) || 0) + 1);
    }
    const order = new Map(nodes.map((n, i) => [n.id, i]));
    const keepIds = new Set(nodes.slice()
      .sort((a, b) => {
        const subj = Number(b.placement_role === 'subject') - Number(a.placement_role === 'subject');
        return subj || (degree.get(b.id) || 0) - (degree.get(a.id) || 0) || order.get(a.id)! - order.get(b.id)!;
      })
      .slice(0, maxNodes).map((n) => n.id));
    omittedNodes = nodes.length - keepIds.size;
    nodes = nodes.filter((n) => keepIds.has(n.id));
    edges = edges.filter((e) => keepIds.has(e.source) && keepIds.has(e.target));
  }

  const byId = new Map(nodes.map((n) => [n.id, n]));

  // ---- Lanes: declared phases when any node has one, else declared roles.
  const phased = nodes.some((n) => typeof n.phase === 'string' && n.phase);
  const laneOf = new Map<string, string>();
  const laneLabels = new Map<string, string>();
  let laneOrder: string[];
  let basis: FlowDiagramLayout['laneBasis'] = 'phase';
  if (phased) {
    const lanesDef = phaseLanes(opts.laneOrder);
    const known = new Set(lanesDef.map(([k]) => k));
    const extras: string[] = [];
    for (const n of nodes) {
      const p = typeof n.phase === 'string' && n.phase ? n.phase.toLowerCase() : NOT_PHASED;
      laneOf.set(n.id, p);
      if (p !== NOT_PHASED && !known.has(p) && !extras.includes(p)) extras.push(p);
    }
    for (const [k, l] of lanesDef) laneLabels.set(k, l);
    for (const x of extras) laneLabels.set(x, x.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()));
    laneLabels.set(NOT_PHASED, 'Not phased');
    laneOrder = [...lanesDef.map(([k]) => k), ...extras.sort(), NOT_PHASED];
  } else if (nodes.some((n) => n.placement_role === 'subject')) {
    // No declared phases: bands by hops from the subject along the drawn edges (upstream negative, downstream positive; a tie goes downstream).
    const outA = new Map<string, string[]>(); const inA = new Map<string, string[]>();
    for (const e of edges) {
      if (!outA.has(e.source)) outA.set(e.source, []);
      outA.get(e.source)!.push(e.target);
      if (!inA.has(e.target)) inA.set(e.target, []);
      inA.get(e.target)!.push(e.source);
    }
    const bfs = (adj: Map<string, string[]>, from: string[]) => {
      const d = new Map<string, number>(from.map((f) => [f, 0]));
      const q = [...from];
      while (q.length) { const c = q.shift()!; for (const nx of adj.get(c) || []) if (!d.has(nx)) { d.set(nx, d.get(c)! + 1); q.push(nx); } }
      return d;
    };
    const subj = nodes.filter((n) => n.placement_role === 'subject').map((n) => n.id);
    const down = bfs(outA, subj); const up = bfs(inA, subj);
    for (const n of nodes) {
      const d = down.get(n.id); const u = up.get(n.id);
      const hop = d === undefined && u === undefined ? null : (u === undefined || (d !== undefined && d <= u) ? d! : -u!);
      laneOf.set(n.id, hop === null ? 'hop:none' : hop < 0 ? 'hop:up' : hop === 0 ? 'hop:0' : 'hop:down');
    }
    laneLabels.set('hop:up', 'Upstream of the subject');
    laneLabels.set('hop:0', 'Subject');
    laneLabels.set('hop:down', 'Downstream of the subject');
    laneLabels.set('hop:none', 'Not connected to the subject');
    laneOrder = ['hop:up', 'hop:0', 'hop:down', 'hop:none'];
    basis = 'hops';
  } else {
    for (const [k, l] of ROLE_LANES) laneLabels.set(k, l);
    for (const n of nodes) {
      const r = n.placement_role === 'shared_relay' ? 'relay' : (n.placement_role || 'context');
      laneOf.set(n.id, laneLabels.has(r) ? r : 'context');
    }
    laneOrder = ROLE_LANES.map(([k]) => k);
    basis = 'role';
  }
  const usedLanes = laneOrder.filter((k) => nodes.some((n) => laneOf.get(n.id) === k));
  const laneIdx = new Map(usedLanes.map((k, i) => [k, i]));
  const L = (id: string) => laneIdx.get(laneOf.get(id)!)!;

  // ---- Back edges: into an earlier lane, or closing a cycle inside a lane (DFS in IR order).
  const back = new Set<number>();
  const sameOut = new Map<string, number[]>();
  edges.forEach((e, i) => {
    if (L(e.target) < L(e.source)) { back.add(i); return; }
    if (L(e.target) === L(e.source)) {
      if (!sameOut.has(e.source)) sameOut.set(e.source, []);
      sameOut.get(e.source)!.push(i);
    }
  });
  const state = new Map<string, number>();
  for (const root of nodes) {
    if (state.has(root.id)) continue;
    const stack: { id: string; next: number }[] = [{ id: root.id, next: 0 }];
    state.set(root.id, 1);
    while (stack.length) {
      const top = stack[stack.length - 1];
      const list = sameOut.get(top.id) || [];
      if (top.next < list.length) {
        const ei = list[top.next++];
        const t = edges[ei].target;
        const st = state.get(t);
        if (st === 1) back.add(ei);
        else if (st === undefined) { state.set(t, 1); stack.push({ id: t, next: 0 }); }
      } else { state.set(top.id, 2); stack.pop(); }
    }
  }

  // ---- Columns: a lane's columns are its longest-path sublayers.
  const sub = new Map<string, number>(nodes.map((n) => [n.id, 0]));
  for (let pass = 0; pass <= nodes.length; pass++) {
    let changed = false;
    edges.forEach((e, i) => {
      if (back.has(i) || L(e.source) !== L(e.target)) return;
      const v = sub.get(e.source)! + 1;
      if (v > sub.get(e.target)!) { sub.set(e.target, v); changed = true; }
    });
    if (!changed) break;
  }
  // Declared sinks with no onward edge close their lane: exits sit at the right edge of it.
  {
    const hasOut = new Set<string>();
    edges.forEach((e, i) => { if (!back.has(i)) hasOut.add(e.source); });
    const hasIn = new Set(edges.map((e) => e.target));
    const laneMax = new Map<string, number>();
    for (const n of nodes) laneMax.set(laneOf.get(n.id)!, Math.max(laneMax.get(laneOf.get(n.id)!) || 0, sub.get(n.id)!));
    for (const n of nodes) if (n.placement_role === 'sink' && hasIn.has(n.id) && !hasOut.has(n.id)) sub.set(n.id, laneMax.get(laneOf.get(n.id)!)!);
  }
  const laneWidth = usedLanes.map((k) => Math.max(0, ...nodes.filter((n) => laneOf.get(n.id) === k).map((n) => sub.get(n.id)!)) + 1);
  const laneStart: number[] = [];
  laneWidth.reduce((s, w, i) => { laneStart[i] = s; return s + w; }, 0);
  const nCols = laneWidth.reduce((a, b) => a + b, 0);
  const colOf = new Map(nodes.map((n) => [n.id, laneStart[L(n.id)] + sub.get(n.id)!]));

  // ---- Items: real nodes plus one waypoint per column an edge crosses.
  const items = new Map<string, Item>();
  nodes.forEach((n, i) => items.set(n.id, { id: n.id, real: true, col: colOf.get(n.id)!, h: boxHeight(n), y: 0, order: i }));
  const chains = new Map<number, string[]>();
  const left = new Map<string, string[]>();
  const right = new Map<string, string[]>();
  const link = (a: string, b: string) => {
    if (!right.has(a)) right.set(a, []);
    right.get(a)!.push(b);
    if (!left.has(b)) left.set(b, []);
    left.get(b)!.push(a);
  };
  edges.forEach((e, i) => {
    if (back.has(i)) return;
    const cs = colOf.get(e.source)!; const ct = colOf.get(e.target)!;
    const chain = [e.source];
    for (let c = cs + 1; c < ct; c++) {
      const id = `~w${i}_${c}`;
      items.set(id, { id, real: false, col: c, h: WAYPOINT_H, y: 0, order: nodes.length + i });
      chain.push(id);
    }
    chain.push(e.target);
    for (let k = 0; k + 1 < chain.length; k++) link(chain[k], chain[k + 1]);
    chains.set(i, chain);
  });

  const columns: Item[][] = Array.from({ length: nCols }, () => []);
  const rank = (it: Item) => (it.real ? (ROLE_RANK[byId.get(it.id)!.placement_role || ''] ?? 2) : 9);
  [...items.values()]
    .sort((a, b) => rank(a) - rank(b) || a.order - b.order)
    .forEach((it) => columns[it.col].push(it));
  const pos = new Map<string, number>();
  const setPos = () => columns.forEach((col) => col.forEach((it, i) => pos.set(it.id, i)));
  setPos();
  const sweep = (c: number, nbrs: Map<string, string[]>) => {
    const key = new Map<string, number>();
    columns[c].forEach((it, i) => {
      const ns = nbrs.get(it.id) || [];
      key.set(it.id, ns.length ? ns.reduce((s, x) => s + pos.get(x)!, 0) / ns.length : i);
    });
    columns[c] = columns[c].map((it, i) => ({ it, i })).sort((a, b) => key.get(a.it.id)! - key.get(b.it.id)! || a.i - b.i).map((x) => x.it);
    setPos();
  };
  for (let round = 0; round < 4; round++) {
    for (let c = 1; c < nCols; c++) sweep(c, left);
    for (let c = nCols - 2; c >= 0; c--) sweep(c, right);
  }

  // ---- Vertical placement.
  const gapOf = (a: Item, b: Item) => (a.real && b.real ? ROW_GAP : WAYPOINT_GAP);
  const centre = (id: string) => items.get(id)!.y + items.get(id)!.h / 2;
  columns.forEach((col) => {
    let y = 0;
    col.forEach((it, i) => { if (i) y += gapOf(col[i - 1], it); it.y = y; y += it.h; });
    const tall = y;
    col.forEach((it) => { it.y -= tall / 2; });
  });
  const desired = (nbrs: Map<string, string[]>[]) => (it: Item) => {
    const ns = nbrs.flatMap((m) => m.get(it.id) || []);
    return ns.length ? ns.reduce((s, x) => s + centre(x), 0) / ns.length : it.y + it.h / 2;
  };
  for (let round = 0; round < 6; round++) {
    for (let c = 1; c < nCols; c++) placeColumn(columns[c], desired([left]), gapOf);
    for (let c = nCols - 2; c >= 0; c--) placeColumn(columns[c], desired([right]), gapOf);
  }
  for (let c = 0; c < nCols; c++) placeColumn(columns[c], desired([left, right]), gapOf);
  const hasBack = back.size > 0;
  const padL = PAD_X + (hasBack ? 40 : 0);
  const minTop = Math.min(0, ...[...items.values()].map((it) => it.y));
  const shift = HEADER_H + PAD_Y - minTop;
  for (const it of items.values()) it.y = r1(it.y + shift);
  const colX = (c: number) => r1(padL + c * COL_W);
  const maxBottom = Math.max(HEADER_H + PAD_Y, ...[...items.values()].filter((it) => it.real).map((it) => it.y + it.h));

  const laid = new Map<string, LaidOutNode>();
  for (const n of nodes) {
    const it = items.get(n.id)!;
    laid.set(n.id, { node: n, layer: it.col, lane: laneOf.get(n.id)!, x: colX(it.col), y: it.y, w: BOX_W, h: it.h });
  }

  // ---- Ports: the edges that meet one side of a node are spread along that side, ordered by where they go.
  const portOut = new Map<number, number>();
  const portIn = new Map<number, number>();
  for (const out of [true, false]) {
    const m = new Map<string, { i: number; key: number }[]>();
    edges.forEach((e, i) => {
      const id = out ? e.source : e.target;
      let key: number;
      if (back.has(i)) key = 1e9 + i;
      else { const ch = chains.get(i)!; key = centre(out ? ch[1] : ch[ch.length - 2]); }
      if (!m.has(id)) m.set(id, []);
      m.get(id)!.push({ i, key });
    });
    for (const [id, list] of m) {
      list.sort((a, b) => a.key - b.key || a.i - b.i);
      const n = laid.get(id)!;
      list.forEach((p, k) => (out ? portOut : portIn).set(p.i, r1(n.y + (n.h * (k + 1)) / (list.length + 1))));
    }
  }

  // ---- Paths.
  const bez = (x0: number, y0: number, x1: number, y1: number): number[][] => {
    const dx = (x1 - x0) / 2;
    return [[x0, y0], [x0 + dx, y0], [x1 - dx, y1], [x1, y1]];
  };
  const curves: number[][][][] = [];
  const runs: [number, number, number][][] = [];
  const slots = new Map<number, number>();
  const nextSlot = (g: number) => { const k = slots.get(g) || 0; slots.set(g, k + 1); return k % 8; };
  const loopY0 = maxBottom + 22;
  const backLabelAt = new Map<number, [number, number]>();
  let loopIdx = 0;
  const laidEdges: LaidOutEdge[] = edges.map((e, i) => {
    const a = laid.get(e.source)!; const b = laid.get(e.target)!;
    const y0 = portOut.get(i)!; const y1 = portIn.get(i)!;
    const segs: number[][][] = []; const rn: [number, number, number][] = [];
    let path: string;
    if (!back.has(i)) {
      const ch = chains.get(i)!;
      let x = a.x + a.w; let y = y0;
      path = `M${r1(x)},${r1(y)}`;
      const add = (s: number[][]) => { segs.push(s); path += ` C${r1(s[1][0])},${r1(s[1][1])} ${r1(s[2][0])},${r1(s[2][1])} ${r1(s[3][0])},${r1(s[3][1])}`; };
      for (let k = 1; k < ch.length - 1; k++) {
        const it = items.get(ch[k])!; const cy = r1(it.y + it.h / 2);
        add(bez(x, y, colX(it.col), cy));
        path += ` L${r1(colX(it.col) + BOX_W)},${cy}`;
        rn.push([colX(it.col), colX(it.col) + BOX_W, cy]);
        x = colX(it.col) + BOX_W; y = cy;
      }
      add(bez(x, y, b.x, y1));
    } else {
      const sx = a.x + a.w; const tx = b.x;
      const xs = r1(sx + 12 + nextSlot(a.layer) * 7);
      const xt = r1(tx - 12 - nextSlot(-1 - b.layer) * 7);
      const ly = r1(loopY0 + loopIdx * 9);
      loopIdx += 1;
      const R = 6;
      path = `M${sx},${y0} L${xs - R},${y0} Q${xs},${y0} ${xs},${y0 + R} L${xs},${ly - R} Q${xs},${ly} ${xs - R},${ly} L${xt + R},${ly} Q${xt},${ly} ${xt},${ly - R} L${xt},${y1 + R} Q${xt},${y1} ${xt + R},${y1} L${tx},${y1}`;
      backLabelAt.set(i, [(xs + xt) / 2, ly]);
    }
    curves[i] = segs; runs[i] = rn;
    return { edge: e, back: back.has(i), path, label: null };
  });

  // ---- Edge labels: placed on the edge in free space, checked against every node and every earlier label.
  const obstacles: Rect[] = [...laid.values()].map((n) => ({ x: n.x - 3, y: n.y - 3, w: n.w + 6, h: n.h + 6 }));
  const hit = (r: Rect) => obstacles.some((o) => r.x < o.x + o.w && r.x + r.w > o.x && r.y < o.y + o.h && r.y + r.h > o.y);
  let omittedLabels = 0;
  laidEdges.forEach((le, i) => {
    const lines = edgeLabelLines(le.edge);
    if (!lines.length) return;
    if (edges.length > EDGE_LABEL_LIMIT) { omittedLabels += 1; return; }
    const w = Math.max(...lines.map((l) => l.length * 5.4)) + 8;
    const h = lines.length * 11.5 + 4;
    const cands: [number, number][] = [];
    if (le.back) {
      const p = backLabelAt.get(i);
      if (p) cands.push(p);
    } else {
      for (const t of [0.5, 0.4, 0.6, 0.3, 0.7]) for (const s of curves[i]) cands.push(cubicAt(s, t));
      for (const [x0, x1, y] of runs[i]) cands.push([(x0 + x1) / 2, y]);
    }
    for (const [cx, cy] of cands) {
      for (const dy of [0, -(h / 2 + 3), h / 2 + 3]) {
        const r = { x: r1(cx - w / 2), y: r1(cy + dy - h / 2), w: r1(w), h: r1(h) };
        if (r.x < 2 || r.y < HEADER_H || hit(r)) continue;
        obstacles.push(r);
        le.label = { ...r, lines };
        return;
      }
    }
    omittedLabels += 1;
  });

  // ---- Lane bands.
  const width = r1(colX(nCols - 1) + BOX_W + GAP / 2);
  const lanes: LaneBand[] = usedLanes.map((k, li) => {
    const c0 = laneStart[li]; const c1 = laneStart[li] + laneWidth[li] - 1;
    const x0 = li === 0 ? 2 : colX(c0) - GAP / 2 + 2;
    const x1 = li === usedLanes.length - 1 ? width - 2 : colX(c1) + BOX_W + GAP / 2 - 2;
    return { key: k, label: laneLabels.get(k)!, x: r1(x0), w: r1(x1 - x0), count: nodes.filter((n) => laneOf.get(n.id) === k).length };
  });
  const height = r1(maxBottom + PAD_Y + (hasBack ? 22 + loopIdx * 9 : 0));
  return {
    nodes: [...laid.values()].sort((a, b) => a.layer - b.layer || a.y - b.y),
    edges: laidEdges,
    lanes,
    width,
    height,
    layers: nCols,
    omittedNodes,
    omittedSelfLoops,
    omittedLabels,
    laneBasis: basis,
  };
}

function styleOf(cls: string | null | undefined) {
  return (cls && DIAGRAM_CLASS_STYLE[cls]) || DEFAULT_STYLE;
}

type Shape = 'stadium' | 'bar' | 'hex' | 'round';
/** Node shape by entity bucket: the shape says what kind of party it is, the colour says the bucket. */
const SHAPE: Record<string, Shape> = {
  subject: 'stadium', victim: 'stadium', cex: 'bar', recipient: 'bar', bridge: 'hex', mixer: 'hex',
};
const SHAPE_NAME: Record<Shape, string> = { stadium: 'pill', bar: 'banner', hex: 'hexagon', round: 'rounded box' };
const shapeOf = (cls: string | null | undefined): Shape => SHAPE[cls || ''] || 'round';

function shapeSvg(shape: Shape, x: number, y: number, w: number, h: number, st: { fill: string; stroke: string; dashed?: boolean }, sw: number): string {
  const dash = st.dashed ? ' stroke-dasharray="4 3"' : '';
  const base = `fill="${st.fill}" stroke="${st.stroke}" stroke-width="${sw}"${dash}`;
  if (shape === 'stadium') return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r1(Math.min(h / 2, 22))}" ${base}/>`;
  if (shape === 'hex') {
    const s = Math.min(12, h / 2);
    return `<polygon points="${x + s},${y} ${x + w - s},${y} ${x + w},${r1(y + h / 2)} ${x + w - s},${y + h} ${x + s},${y + h} ${x},${r1(y + h / 2)}" ${base}/>`;
  }
  if (shape === 'bar') return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2" ${base}/><rect x="${x}" y="${y}" width="${w}" height="5" rx="2" fill="${st.stroke}"/>`;
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="6" ${base}/>`;
}

/** The SVG only. `idPrefix` keeps marker ids unique when several diagrams share a page. */
export function renderFlowDiagramSvg(ir: FlowDiagramIR, opts: { idPrefix: string; title: string; maxNodes?: number; laneOrder?: LaneOrder }): string {
  const lay = layoutFlowDiagram(ir, { maxNodes: opts.maxNodes, laneOrder: opts.laneOrder });
  const pid = opts.idPrefix.replace(/[^a-zA-Z0-9_-]/g, '_');
  const bands = lay.lanes.map((l, i) => {
    const np = isUnplaced(l.key);
    return `<g class="dg-lane" data-lane="${esc(l.key)}"><rect x="${l.x}" y="2" width="${l.w}" height="${r1(lay.height - 4)}" rx="8" fill="${np ? '#fff7ed' : i % 2 ? '#f1f5f9' : '#f8fafc'}" stroke="${np ? '#fdba74' : '#e2e8f0'}"${np ? ' stroke-dasharray="5 4"' : ''}/>`
      + `<text x="${r1(l.x + l.w / 2)}" y="21" text-anchor="middle" class="dg-lane-label">${esc(l.label.toUpperCase())}</text></g>`;
  }).join('');
  const edgeSvg = lay.edges.map(({ edge, path, label }) => {
    const ls = LINE_KIND_STYLE[edge.line_kind || 'solid'] || LINE_KIND_STYLE.solid;
    const dash = ls.dash ? ` stroke-dasharray="${ls.dash}"` : '';
    const title = (edge.label_lines || []).join(' | ');
    const lab = label
      ? `<rect x="${label.x}" y="${label.y}" width="${label.w}" height="${label.h}" rx="3" class="dg-elabel-bg"/>`
        + label.lines.map((t, k) => `<text x="${r1(label.x + label.w / 2)}" y="${r1(label.y + 11 + k * 11.5)}" text-anchor="middle" class="dg-elabel">${esc(t)}</text>`).join('')
      : '';
    return `<g class="dg-edge"><title>${esc(title || 'transfer')}</title><path d="${path}" fill="none" stroke="#475569" stroke-width="${ls.width}"${dash} marker-end="url(#${pid}-arrow)"/>${lab}</g>`;
  }).join('');
  const nodeSvg = lay.nodes.map(({ node, x, y, w, h }) => {
    const st = styleOf(node.entity_class);
    const lines = (node.label_lines && node.label_lines.length ? node.label_lines : [node.id]).slice(0, MAX_LABEL_LINES);
    const shape = shapeOf(node.entity_class);
    const strong = node.placement_role === 'subject' ? 2.6 : 1.5;
    const tx = x + (shape === 'hex' ? 16 : shape === 'stadium' ? 14 : 10);
    const text = lines.map((l, i) =>
      `<text x="${r1(tx)}" y="${r1(y + 18 + i * LINE_H)}" class="${i === 0 ? 'dg-nlabel' : 'dg-nsub'}" fill="${st.text}">${esc(clipText(l, 22))}</text>`).join('');
    const title = `${(nodeLineList(node).length ? nodeLineList(node) : [node.id]).join(' | ')}${node.recipient_address ? ` | notice recipient wallet ${node.recipient_address}` : ''}${node.group ? ` [${node.group}]` : ''}${node.phase ? ` (phase: ${node.phase})` : ''} — ${node.placement_role || 'role not declared'}${node.entity_class ? `, ${styleOf(node.entity_class).label.toLowerCase()}` : ''}`;
    return `<g class="dg-node" data-role="${esc(node.placement_role || '')}"><title>${esc(title)}</title>${shapeSvg(shape, x, y, w, h, st, strong)}${text}</g>`;
  }).join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" class="dg-svg" viewBox="0 0 ${lay.width} ${lay.height}" width="${lay.width}" height="${lay.height}" role="img" aria-label="${esc(opts.title)}">`
    + `<defs><marker id="${pid}-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="9" markerHeight="9" markerUnits="userSpaceOnUse" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="#475569"/></marker></defs>`
    + bands + edgeSvg + nodeSvg + '</svg>';
}

/** Legend for the buckets and line kinds this diagram actually uses. */
export function renderFlowDiagramLegend(ir: FlowDiagramIR, laneOrder?: LaneOrder): string {
  const classes = [...new Set((ir.nodes || []).map((n) => n.entity_class).filter((c): c is string => !!c))];
  const ordered = Object.keys(DIAGRAM_CLASS_STYLE).filter((c) => classes.includes(c));
  const unknown = (ir.nodes || []).some((n) => !n.entity_class || !DIAGRAM_CLASS_STYLE[n.entity_class]);
  const kinds = [...new Set((ir.edges || []).map((e) => e.line_kind || 'solid'))];
  const kindOrder = Object.keys(LINE_KIND_STYLE).filter((k) => kinds.includes(k));
  const swatch = (s: { fill: string; stroke: string; dashed?: boolean }, shape: Shape) =>
    `<svg class="dg-swatch" width="24" height="14" viewBox="0 0 24 14" aria-hidden="true">${shapeSvg(shape, 1, 1, 22, 12, s, 1.4)}</svg>`;
  const line = (k: string) => {
    const ls = LINE_KIND_STYLE[k];
    return `<svg class="dg-line" width="34" height="10" aria-hidden="true"><line x1="1" y1="5" x2="33" y2="5" stroke="#475569" stroke-width="${ls.width}"${ls.dash ? ` stroke-dasharray="${ls.dash}"` : ''}/></svg>`;
  };
  const items = [
    ...ordered.map((c) => `<li>${swatch(DIAGRAM_CLASS_STYLE[c], shapeOf(c))}${esc(DIAGRAM_CLASS_STYLE[c].label)}</li>`),
    ...(unknown ? [`<li>${swatch(DEFAULT_STYLE, 'round')}Other or not classified</li>`] : []),
    ...kindOrder.map((k) => `<li>${line(k)}${esc(LINE_KIND_STYLE[k].label)}</li>`),
  ];
  const shapes = [...new Set(ordered.map((c) => shapeOf(c)))].map((s) => SHAPE_NAME[s]);
  const lanes = layoutFlowDiagram(ir, { laneOrder }).lanes.map((l) => l.label);
  return `<div class="dg-legend" data-testid="diagram-legend"><strong>Legend</strong><ul>${items.join('')}</ul>
    <p class="muted">Bands run left to right in the direction money moved${lanes.length ? ` (${esc(lanes.join(', ').toLowerCase())})` : ''}. A box with a thick outline is the subject.${shapes.length > 1 ? ` Shape marks the kind of party (${esc(shapes.join(', '))}); colour marks the bucket.` : ''} Bands come from the phases declared in the data (or, when none are declared, from direction from the subject along the drawn edges), not from the drawing; a node with no declared phase sits in the "Not phased" band.</p>${hasFlowFacts(ir) ? `<p class="muted" data-testid="diagram-legend-facts">${flowFactsLegend(ir)}</p>` : ''}</div>`;
}

/**
 * A figure whose drawn width over height exceeds this prints on its own landscape page (`page: wide`); a taller one stays
 * portrait and is scaled to the page. 1.3 is the starting value (FLOW01 owner decision): a landscape text block is about
 * 1.55 wide for its height and a portrait one about 0.7, so 1.3 sends the clearly wide drawings to landscape and keeps
 * the squarish ones, which fit a portrait page after scaling, in the flow of the text.
 */
export const WIDE_FIGURE_RATIO = 1.3;

/**
 * One diagram as a report figure: heading, SVG, legend, layout notes, and the
 * Mermaid source in a collapsed block with a Copy button.
 */
export function renderFlowDiagramFigure(ir: FlowDiagramIR, opts: { id: string; title: string; caption?: string; maxNodes?: number; laneOrder?: LaneOrder }): string {
  const lay = layoutFlowDiagram(ir, { maxNodes: opts.maxNodes, laneOrder: opts.laneOrder });
  const notes: string[] = [];
  if (lay.omittedNodes) notes.push(`Limited to the ${lay.nodes.length} most connected nodes; ${lay.omittedNodes} more are in the Mermaid source and the appendix.`);
  if (lay.omittedSelfLoops) notes.push(`${lay.omittedSelfLoops} self-transfer(s) not drawn.`);
  if (lay.omittedLabels) notes.push(`${lay.omittedLabels} edge label(s) had no free space and are not drawn; hover the edge on screen, or see the Mermaid source and the appendix.`);
  if (lay.edges.some((e) => e.back)) notes.push('Edges that run back against the flow are drawn below the nodes.');
  if (lay.lanes.some((l) => l.key === NOT_PHASED)) notes.push('Nodes in the "Not phased" band carry no declared phase in the data.');
  if (lay.laneBasis === 'hops') notes.push('This run declares no phases for this diagram; the bands split the nodes by direction from the subject along the drawn edges.');
  const svg = renderFlowDiagramSvg(ir, { idPrefix: opts.id, title: opts.title, maxNodes: opts.maxNodes, laneOrder: opts.laneOrder });
  const source = typeof ir.mermaid === 'string' && ir.mermaid.trim()
    ? buildCollapsedCodeBlock(`${opts.id}-mermaid`, 'Mermaid source (paste into any Mermaid or flowchart editor to redraw)', ir.mermaid)
    : '<p class="muted">No Mermaid source for this diagram.</p>';
  const wide = lay.height > 0 && lay.width / lay.height > WIDE_FIGURE_RATIO;
  // The wallet table and the Mermaid source follow the figure, outside it: the figure (caption, drawing, legend) is the block
  // kept on one page, and a 40-row table inside it would be longer than a page.
  return `<figure class="dg-figure${wide ? ' fig-wide' : ''}" id="${esc(opts.id)}" data-testid="flow-diagram-${esc(opts.id)}" data-orientation="${wide ? 'landscape' : 'portrait'}">
    <figcaption><strong>${esc(opts.title)}</strong>${opts.caption ? ` <span class="muted">${esc(opts.caption)}</span>` : ''}
      <span class="muted">(${(ir.nodes || []).length} node(s), ${(ir.edges || []).length} edge(s))</span></figcaption>
    <div class="svg-wrap">${svg}</div>
    ${renderFlowDiagramLegend(ir, opts.laneOrder)}
    ${notes.length ? `<p class="muted">${esc(notes.join(' '))}</p>` : ''}
  </figure>
  ${figureWalletBlock(ir, opts.id)}
  ${source}`;
}

export const FLOW_DIAGRAM_CSS = `
figure.dg-figure { margin: 10px 0 22px; break-inside: avoid; page-break-inside: avoid; }
figure.dg-figure figcaption { margin: 0 0 6px; font-size: 12.5px; }
.svg-wrap .dg-svg { max-width: 100%; height: auto; min-width: 900px; }
.dg-svg text.dg-lane-label { font: 700 10.5px Inter, system-ui, sans-serif; fill: #475569; letter-spacing: .06em; }
.dg-svg text.dg-nlabel { font: 600 11px Inter, system-ui, sans-serif; }
.dg-svg text.dg-nsub { font: 10.5px Inter, system-ui, sans-serif; }
.dg-svg text.dg-elabel { font: 10px Inter, system-ui, sans-serif; fill: #1e293b; }
.dg-svg rect.dg-elabel-bg { fill: #fff; fill-opacity: .92; stroke: #e2e8f0; stroke-width: .6; }
.dg-legend { font-size: 11px; margin: 6px 0; }
.dg-legend ul { list-style: none; margin: 4px 0; padding: 0; display: flex; flex-wrap: wrap; gap: 4px 16px; }
.dg-legend li { display: inline-flex; align-items: center; gap: 6px; }
.dg-swatch { display: inline-block; vertical-align: middle; }
.dg-svg, .dg-swatch, .dg-line { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.print-only { display: none; }
details.wallet-details { margin: 4px 0 10px; font-size: 11px; }
details.wallet-details > summary { cursor: pointer; color: #475569; }
@media print {
  @page wide { size: A4 landscape; margin: 14mm 10mm 18mm 10mm; }
  .print-only { display: block; }
  /* A wide drawing or a wide table gets its own landscape page; prose stays portrait. */
  figure.dg-figure.fig-wide, .wide-page { page: wide; }
  /* The drawing is scaled to the page in both orientations (height capped so figure, caption and legend fit one page). */
  figure.dg-figure .svg-wrap .dg-svg { max-width: 100%; min-width: 0; width: 100%; height: auto; max-height: 185mm; }
  figure.dg-figure.fig-wide .svg-wrap .dg-svg { max-height: 120mm; }
  .wide-page table.data { width: 100%; }
}
`;
